import { defineRecipe } from '@chakra-ui/react';

const keyboardFocusRing = {
  outlineWidth: '2px',
  outlineStyle: 'solid',
  outlineColor: 'purple.focusRing',
  outlineOffset: '2px',
} as const;

/**
 * Плита — поверхность, на которой лежит содержимое страницы.
 *
 * Заводится рецептом по той же причине, что и чип: общий компонент можно не позвать, а рецепт
 * достаётся вместе с темой. До этого плита собиралась руками в сотне мест, и замер живых экранов
 * дал двадцать два сочетания материала, края, скругления и тени: выбор делался на каждом экране
 * заново и делался иначе.
 *
 * Три роли отвечают на вопрос «чем эта коробка является», а не «как она выглядит»:
 *
 * - `object` — то, что человек воспринимает как отдельный предмет и может открыть: карточка
 *   заявки, плитка игры, карточка человека. Держится границей: у предмета есть край.
 * - `container` — колонка или список, который держит разнородное: оболочка страницы дашборда,
 *   плиты профиля, списки друзей. Держится тенью: у контейнера не край, а глубина.
 * - `nested` — блок внутри объекта или контейнера: цитата в ответе, состав сессии в карточке,
 *   полоса цифр в шапке. Только заливка, иначе вложенность читается как ещё одна коробка того
 *   же уровня.
 * - `quiet` — та же вложенность, но там, где блок не заявляет о себе, а только не даёт
 *   содержимому слипнуться с соседями. Заведена под текст заявки в карточке ленты: `nested`
 *   даёт 1.19:1 к фону карточки, и описание читалось серым прямоугольником внутри серой
 *   карточки, наравне с полосой сессии и комментариями под ним. Здесь 1.11:1 — форма блока
 *   ещё различима, но внимание он не тянет, и иерархию держит текст над ним, а не подложка.
 *
 *   Почему роль, а не переопределение фона в вызове: `nested` стоит в 24 местах продукта
 *   (чат, комментарии, настройки, диалоги сессии, блог), и подкрутка под один экран сделала бы
 *   его 29-м исключением — через месяц роль перестала бы что-либо значить. Роль отвечает на
 *   вопрос «чем коробка является», и «вложенный блок, который молчит» это отдельный ответ.
 *
 * `flat` — не роль, а адаптивный выход: на телефоне плита растворяется в
 * страницу, и шесть мест описывали это пятью адаптивными пропами каждое, причём чаты с порогом
 * `lg`, а остальные с `md`. Теперь это `variant={{ base: 'flat', md: 'container' }}`. Значение
 * обязано объявлять все свойства, которые объявляет роль: адаптивный вариант работает только
 * потому, что базовое правило перекрывается медиа-правилом по тем же самым свойствам.
 *
 * Скругления: 16 у контейнера, 12 у объекта, 8 у вложенного. Контейнер крупнее объекта не ради
 * шкалы, а потому что держит объекты внутри с отступом 24: при 8 снаружи и 12 внутри вложенное
 * оказывалось круглее коробки, в которой лежит. Дашборд стоял на 8, профиль на 12, страница
 * заявки на 16 — одна роль, три числа. Число здесь одно.
 *
 * `size` заведён потому, что отступ задавал вызывающий: из 53 самодельных плит 44 задавали его
 * сами, разбросом от 8 до 40. Реальных шагов оказалось три, по одному на роль. Дефолт `none` с
 * явным нулём: существующие вызовы передают свои `px/py` пропами, а пропы лежат выше слоя
 * рецепта и побеждают.
 *
 * `interactive` и `selected`: десять плит нажимаются, и ховер у них был четырёх направлений,
 * включая одно обратное — темнее при наведении. Направление одно: на ступень светлее. Фокус с
 * клавиатуры не рисовала ни одна из десяти.
 *
 * Состояния лежат в `compoundVariants`, а не внутри осей: compound-стили сливаются после стилей
 * ролей и гарантированно перекрывают заливку. Плата за это — compound сравнивает значение строго
 * и не совпадает с адаптивным `variant`. Интерактивная плита адаптивной не бывает: растворяются
 * в страницу только контейнеры.
 */
export const panelRecipe = defineRecipe({
  className: 'tp-panel',
  base: {
    minWidth: 0,
  },
  variants: {
    variant: {
      object: {
        bgColor: 'bg.card',
        borderWidth: '1px',
        borderColor: 'border.subtle',
        borderRadius: 'xl',
        boxShadow: 'card',
      },
      container: {
        bgColor: 'bg.surface',
        borderWidth: '0',
        boxShadow: 'cardOutline',
        borderRadius: '2xl',
      },
      nested: {
        bgColor: 'bg.muted',
        borderWidth: '0',
        boxShadow: 'none',
        borderRadius: 'lg',
      },
      // Тихая вложенность: всё как у `nested`, кроме заливки на ступень ниже (`bg.subtle`,
      // 4% белого против 6%). Остальные свойства повторены явно, а не унаследованы: роли
      // рецепта не наследуются друг от друга, и пропуск свойства оставил бы его на значении
      // соседней роли при адаптивной паре.
      quiet: {
        bgColor: 'bg.subtle',
        borderWidth: '0',
        boxShadow: 'none',
        borderRadius: 'lg',
      },
      // Не роль: плита, растворённая в страницу. Только в адаптивной паре с container.
      flat: {
        bgColor: 'transparent',
        borderWidth: '0',
        boxShadow: 'none',
        borderRadius: '0',
      },
    },
    size: {
      // Явный ноль, а не пустой объект: адаптивная пара `{ base: 'none', md: 'lg' }` работает
      // только когда базовое значение объявляет те же свойства, что и старшее.
      none: { px: 0, py: 0 },
      // Вложенное: цитата, состав сессии, текст заявки в карточке ленты.
      sm: { px: 3, py: 2.5 },
      // Объект: карточка с заголовком и действием.
      md: { px: 4, py: 3 },
      // Контейнер: оболочка страницы, плита профиля.
      lg: { px: 6, py: 5 },
    },
    interactive: {
      true: {
        cursor: 'pointer',
        transitionProperty: 'background-color, border-color',
        transitionDuration: 'fast',
        transitionTimingFunction: 'ease',
        // Outline, а не тень: у контейнера тень занята глубиной, и фокус через box-shadow её
        // стирал бы.
        _focusVisible: keyboardFocusRing,
        '&:has(> .chakra-linkbox__overlay:focus-visible)': keyboardFocusRing,
      },
    },
    selected: {
      true: {},
    },
  },
  compoundVariants: [
    {
      variant: 'object',
      interactive: true,
      css: {
        // `bg.raised`, а не `bg.emphasized`: одна ступень над покоем, как у всего остального.
        _hover: { bgColor: 'bg.raised', borderColor: 'border' },
        _active: { bgColor: 'bg.card' },
      },
    },
    {
      variant: 'container',
      interactive: true,
      css: {
        _hover: { bgColor: 'bg.card' },
        _active: { bgColor: 'bg.surface' },
      },
    },
    {
      // whiteAlpha.300, а не следующая ступень whiteAlpha.200: между 6% и 8% белого 1.02:1,
      // такого ховера нет. 16% — это 1.60:1 к странице, для наведения ровно столько и нужно.
      variant: 'quiet',
      interactive: true,
      css: {
        _hover: { bgColor: 'whiteAlpha.300' },
      },
    },
    {
      variant: 'nested',
      interactive: true,
      css: {
        _hover: { bgColor: 'whiteAlpha.300' },
        _active: { bgColor: 'bg.muted' },
      },
    },
    {
      // Выбранное перекрывает и покой, и ховер: выбранная плита на наведение не реагирует,
      // иначе она «снимается» под курсором.
      selected: true,
      css: {
        borderWidth: '1px',
        borderColor: 'border.selected',
        bgColor: 'bg.selected',
        _hover: { bgColor: 'bg.selected', borderColor: 'border.selected' },
        _active: { bgColor: 'bg.selected' },
      },
    },
  ],
  defaultVariants: {
    variant: 'object',
    size: 'none',
  },
});
